/* ============================================================================
   TIPOS DE PUNTO PROPIO

   ══════════════════════════════════════════════════════════════════════════
   POR QUÉ HACEN FALTA

   La base de puntos del operador guardaba nombre y coordenadas, y nada más.
   Un KMZ de 41 estructuras de línea de transmisión entra como 41 filas
   iguales: ni el mapa ni la tabla distinguen una torre de la zona de acopio ni
   del helipuerto de la base.

   En una operación de línea larga eso no es cosmético. **La zona donde se
   puede posar y la torre donde no se puede son el mismo punto en pantalla**, y
   la diferencia se decide en vuelo mirando afuera.

   ══════════════════════════════════════════════════════════════════════════
   ESTO NO ES SIMBOLOGÍA CERTIFICADA

   Los símbolos de una carta aeronáutica son lenguaje publicado por la
   autoridad. Acá se usan formas simples y propias, pensadas para distinguir de
   un vistazo, **y la aplicación no afirma que coincidan con ninguna carta**.

   Es la misma decisión que con el tráfico: no se imita la simbología TCAS
   porque un piloto que la reconoce le atribuye un significado certificado que
   esta aplicación no puede respaldar.

   ══════════════════════════════════════════════════════════════════════════
   LA FORMA MANDA, NO EL COLOR

   Cada tipo tiene una FORMA propia además de su color. Con sol encima de la
   pantalla el color se lava, y en el tema nocturno los colores se apagan a
   propósito. Un juego de símbolos que solo se distingue por color no se
   distingue cuando hace falta.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

/**
 * Los tipos, en el orden en que se ofrecen.
 *
 * `operativo` marca si el punto es un lugar donde la aeronave puede estar en el
 * terreno. Es lo que separa «acá puedo posar» de «acá hay algo que esquivar», y
 * **no se deduce del nombre**: una torre se puede llamar «PLATAFORMA 12».
 */
export const TIPOS_DE_PUNTO = [
  {
    id: "generico",
    nombre: "Punto de referencia",
    forma: "circulo",
    color: "#5BD1D7",
    operativo: null,
    descripcion: "Sin clasificar. Es como entra todo lo que viene de un archivo.",
  },
  {
    id: "helipuerto",
    nombre: "Helipuerto",
    forma: "hache",
    color: "#4caf7d",
    operativo: true,
    descripcion: "Helipuerto o helisuperficie preparada y conocida.",
  },
  {
    id: "zonaAterrizaje",
    nombre: "Zona de aterrizaje",
    forma: "cuadrado",
    color: "#4caf7d",
    operativo: true,
    descripcion: "Claro o plataforma de faena donde se puede posar. "
      + "Lo que la aplicación guarda es la posición, no que esté habilitada.",
  },
  {
    id: "acopio",
    nombre: "Zona de acopio",
    forma: "rombo",
    color: "#f2a93b",
    operativo: true,
    descripcion: "Donde se carga y descarga la eslinga.",
  },
  {
    id: "estructura",
    nombre: "Estructura / torre",
    forma: "torre",
    color: "#FFD23F",
    operativo: false,
    descripcion: "Torre de línea de transmisión, antena o estructura alta. "
      + "Es un punto de trabajo Y un obstáculo.",
  },
  {
    id: "obstaculo",
    nombre: "Obstáculo",
    forma: "aspa",
    color: "#e4573d",
    operativo: false,
    descripcion: "Cable, tendido, cumbre o cualquier cosa que haya que esquivar.",
  },
  {
    id: "combustible",
    nombre: "Combustible",
    forma: "gota",
    color: "#5BD1D7",
    operativo: true,
    descripcion: "Punto de reabastecimiento.",
  },
  {
    id: "referenciaVisual",
    nombre: "Referencia visual",
    forma: "triangulo",
    color: "#93A3B3",
    operativo: null,
    descripcion: "Confluencia, puente, poblado: sirve para ubicarse, no para posar.",
  },
];

/** El que se usa mientras nadie clasifique el punto. */
export const TIPO_POR_DEFECTO = "generico";

/**
 * El tipo de un punto, siempre uno válido.
 *
 * **Un tipo desconocido cae en el genérico, no se descarta.** Si una versión
 * futura guardó un tipo que esta no conoce, perder el punto sería mucho peor
 * que mostrarlo sin clasificar: el punto es un dato del operador.
 */
export function tipoDePunto(punto) {
  const id = punto?.tipo;
  return TIPOS_DE_PUNTO.find((t) => t.id === id) || TIPOS_DE_PUNTO[0];
}

/**
 * ¿Se puede posar acá?
 *
 * Devuelve `true`, `false` o **`null` cuando no se sabe**, que es lo que pasa
 * con un punto sin clasificar. No saber no es saber que no: un claro sin
 * clasificar puede ser perfectamente aterrizable, y marcarlo como «no» haría
 * que el piloto descarte una opción real.
 */
export function esOperativo(punto) {
  return tipoDePunto(punto).operativo;
}

/**
 * Los puntos que se pueden dibujar.
 *
 * Un punto sin coordenadas finitas no se dibuja: `Number(null)` es CERO y es
 * finito, así que un punto sin posición caería en el golfo de Guinea con cara
 * de dato bueno. Pasó cinco veces en este proyecto.
 */
export function puntosDibujables(puntos) {
  if (!Array.isArray(puntos)) return [];
  return puntos.filter((p) => (
    p && typeof p.lat === "number" && Number.isFinite(p.lat)
    && typeof p.lon === "number" && Number.isFinite(p.lon)
  ));
}

/**
 * Cuántos puntos hay de cada tipo.
 *
 * Sirve para la leyenda: con 41 estructuras y 2 helipuertos, el piloto quiere
 * saber de un vistazo qué está mirando.
 */
export function conteoPorTipo(puntos) {
  const salida = new Map();
  for (const p of puntosDibujables(puntos)) {
    const t = tipoDePunto(p);
    salida.set(t.id, (salida.get(t.id) || 0) + 1);
  }
  return TIPOS_DE_PUNTO
    .filter((t) => salida.has(t.id))
    .map((t) => ({ ...t, cuantos: salida.get(t.id) }));
}

/**
 * El dibujo de cada forma, en SVG, centrado en (0,0).
 *
 * Se devuelve el marcado y no un componente para que sirva igual en el popup
 * de Leaflet —que recibe HTML— y en React. Un juego de símbolos duplicado en
 * dos lugares se separa con el tiempo, y entonces el mapa y la tabla muestran
 * cosas distintas para el mismo punto.
 */
export function svgDeForma(forma, { tamano = 18, color = "#5BD1D7" } = {}) {
  const r = tamano / 2;
  const trazo = `stroke="${color}" stroke-width="2" fill="none"`;
  const relleno = `fill="${color}"`;

  const cuerpo = {
    circulo: `<circle cx="0" cy="0" r="${r * 0.62}" ${trazo} />`,

    /* La H del helipuerto dentro de su círculo: es la que todo el mundo
       reconoce, y acá va sin pretender ser la de una carta. */
    hache: `<circle cx="0" cy="0" r="${r * 0.86}" ${trazo} />`
      + `<path d="M${-r * 0.3} ${-r * 0.42} V${r * 0.42} M${r * 0.3} ${-r * 0.42} V${r * 0.42} `
      + `M${-r * 0.3} 0 H${r * 0.3}" ${trazo} stroke-linecap="round" />`,

    cuadrado: `<rect x="${-r * 0.6}" y="${-r * 0.6}" width="${r * 1.2}" height="${r * 1.2}" ${trazo} />`,

    rombo: `<path d="M0 ${-r * 0.78} L${r * 0.78} 0 L0 ${r * 0.78} L${-r * 0.78} 0 Z" ${trazo} />`,

    /* La torre se dibuja como una torre: dos patas y dos travesaños. A un
       piloto de línea larga le dice qué es sin leer el rótulo. */
    torre: `<path d="M${-r * 0.55} ${r * 0.8} L${-r * 0.2} ${-r * 0.8} `
      + `M${r * 0.55} ${r * 0.8} L${r * 0.2} ${-r * 0.8} `
      + `M${-r * 0.42} ${r * 0.15} H${r * 0.42} `
      + `M${-r * 0.3} ${-r * 0.35} H${r * 0.3}" ${trazo} stroke-linecap="round" />`,

    aspa: `<path d="M${-r * 0.6} ${-r * 0.6} L${r * 0.6} ${r * 0.6} `
      + `M${r * 0.6} ${-r * 0.6} L${-r * 0.6} ${r * 0.6}" ${trazo} stroke-linecap="round" />`,

    gota: `<path d="M0 ${-r * 0.8} C${r * 0.7} ${-r * 0.1} ${r * 0.52} ${r * 0.78} 0 ${r * 0.78} `
      + `C${-r * 0.52} ${r * 0.78} ${-r * 0.7} ${-r * 0.1} 0 ${-r * 0.8} Z" ${trazo} />`,

    triangulo: `<path d="M0 ${-r * 0.75} L${r * 0.72} ${r * 0.6} L${-r * 0.72} ${r * 0.6} Z" ${trazo} />`,
  }[forma];

  /* Una forma desconocida se dibuja como un punto: el punto EXISTE y tiene que
     verse. Devolver nada lo borraría del mapa por un dato de clasificación. */
  return cuerpo || `<circle cx="0" cy="0" r="${r * 0.35}" ${relleno} />`;
}

/**
 * El marcador completo de un punto, listo para `L.divIcon`.
 *
 * Lleva un halo oscuro detrás: sobre satelital claro, un trazo de color sin
 * contorno desaparece. Es la misma razón por la que los rótulos del mapa
 * llevan `paintOrder="stroke"`.
 */
export function iconoDePunto(punto, { tamano = 22 } = {}) {
  const t = tipoDePunto(punto);
  const mitad = tamano / 2;

  return `<svg width="${tamano}" height="${tamano}" viewBox="${-mitad} ${-mitad} ${tamano} ${tamano}" `
    + `xmlns="http://www.w3.org/2000/svg" aria-hidden="true">`
    + `<g stroke="#0A1420" stroke-width="4.5" fill="none" opacity="0.85">`
    + `${svgDeForma(t.forma, { tamano, color: "#0A1420" })}</g>`
    + svgDeForma(t.forma, { tamano, color: t.color })
    + `</svg>`;
}

/* ═══════════════════════════════════════════════════════════════════════════
   CLASIFICAR UN ARCHIVO ENTERO

   Un KMZ trae puntos de UNA cosa: 41 torres de una línea, o los helipuertos de
   la región, o los waypoints de un plan. Clasificarlos de a uno son 41 clics
   para repetir el mismo dato.

   ══════════════════════════════════════════════════════════════════════════
   LA SUGERENCIA NO SE APLICA SOLA, Y MENOS HACIA «ACÁ SE PUEDE POSAR»

   El nombre del archivo da una pista buena pero no es un dato: un KMZ llamado
   «Plataformas.kmz» puede traer las bases de las torres, que son plataformas y
   no son lugares para posar.

   Por eso la sugerencia se MUESTRA y el operador confirma. Y hay una asimetría
   deliberada en qué se preselecciona:

   · Un tipo que NO afirma que se puede posar —estructura, obstáculo,
     referencia— se puede preseleccionar: si la sugerencia erró, el error es
     conservador.
   · Un tipo OPERATIVO —helipuerto, zona de aterrizaje, acopio, combustible—
     **nunca se preselecciona**. Adivinar hacia «acá se puede posar» es el
     error que esta aplicación no puede cometer: un piloto que ve 41 marcas de
     helipuerto sobre 41 torres tiene 41 trampas en el mapa.

   Las palabras se buscan en el nombre del archivo Y en los nombres de los
   puntos: un archivo llamado `datos.kmz` con puntos «TORRE 12» se reconoce
   igual.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Palabras que delatan cada tipo, en el orden en que se prueban.
 *
 * El orden importa: «helipuerto de faena» tiene las dos palabras, y gana la
 * primera que coincida. Lo más específico va antes.
 */
export const PISTAS_DE_TIPO = [
  { tipo: "estructura", palabras: ["estructura", "torre", "poste", "apoyo", "pilone", "antena", "vertice"] },
  { tipo: "obstaculo", palabras: ["obstaculo", "obstacle", "cable", "tendido", "linea viva", "cumbre"] },
  { tipo: "helipuerto", palabras: ["helipuerto", "heliport", "helipad", "helisuperficie"] },
  { tipo: "zonaAterrizaje", palabras: ["aterrizaje", "landing", "zona de trabajo", "plataforma", "claro", "dz", "lz"] },
  { tipo: "acopio", palabras: ["acopio", "stockpile", "carga", "eslinga", "long line"] },
  { tipo: "combustible", palabras: ["combustible", "fuel", "reabastec", "grifo"] },
  { tipo: "referenciaVisual", palabras: ["referencia", "waypoint", "wpt", "punto de paso", "ruta", "track"] },
];

/** Quita tildes y pasa a minúsculas: «Estructuras» y «ESTRUCTURAS» son lo mismo. */
function normalizar(texto) {
  return String(texto ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

/**
 * Qué tipo parece traer un archivo, con el motivo y de dónde salió la pista.
 *
 * Devuelve `null` cuando no reconoce nada: **inventar un tipo sería peor que
 * dejarlos sin clasificar**, porque sin clasificar la aplicación no afirma
 * nada y con un tipo equivocado afirma algo falso.
 */
export function sugerirTipoDeArchivo(nombreArchivo, puntos = []) {
  const delArchivo = normalizar(nombreArchivo);

  /* Los nombres de los puntos, para cuando el archivo se llama `datos.kmz`.
     Se miran unos pocos: con 41 puntos iguales, los primeros ya lo dicen. */
  const delosPuntos = normalizar(
    (Array.isArray(puntos) ? puntos : []).slice(0, 8).map((p) => p?.nombre || "").join(" "),
  );

  for (const pista of PISTAS_DE_TIPO) {
    for (const palabra of pista.palabras) {
      const enArchivo = delArchivo.includes(palabra);
      const enPuntos = delosPuntos.includes(palabra);
      if (!enArchivo && !enPuntos) continue;

      const t = TIPOS_DE_PUNTO.find((x) => x.id === pista.tipo);

      return {
        tipo: pista.tipo,
        nombre: t.nombre,
        palabra,
        donde: enArchivo ? "archivo" : "puntos",
        motivo: enArchivo
          ? `El nombre del archivo dice «${palabra}».`
          : `Los puntos se llaman con «${palabra}».`,
        /* Lo que decide si se preselecciona. Un tipo operativo afirma que se
           puede posar, y eso no se adivina. */
        operativo: t.operativo === true,
        sePreselecciona: t.operativo !== true,
      };
    }
  }

  return null;
}

/**
 * Pone el mismo tipo a todos los puntos de un archivo.
 *
 * Devuelve una lista NUEVA: los puntos de otros archivos se conservan tal cual,
 * y los del archivo nombrado quedan con el tipo. Un archivo que no existe no
 * cambia nada y no rompe.
 *
 * `cuantos` dice a cuántos les tocó: sin eso, el operador no sabe si el botón
 * hizo algo. Un «listo» sobre cero puntos es peor que un error.
 */
export function clasificarArchivo(puntos, archivo, tipo) {
  const lista = Array.isArray(puntos) ? puntos : [];

  /* Un tipo que no existe no se escribe: dejaría a los puntos con una
     clasificación que ninguna pantalla sabe dibujar. */
  const valido = TIPOS_DE_PUNTO.some((t) => t.id === tipo);
  if (!valido || !archivo) return { puntos: lista, cuantos: 0 };

  let cuantos = 0;
  const salida = lista.map((p) => {
    if (!p || p.archivo !== archivo) return p;
    cuantos += 1;
    return { ...p, tipo };
  });

  return { puntos: salida, cuantos };
}
